import {
  AnchoredStep,
  NoraHomeWalkthroughStep,
  NoraHomeWalkthroughPresentation,
  NoraHomeWalkthroughStepId,
  NoraHomeWalkthroughTargetId,
} from './types'

export const steps: NoraHomeWalkthroughStep[] = [
  {
    id: NoraHomeWalkthroughStepId.Introduction,
    presentation: NoraHomeWalkthroughPresentation.Standalone,
    title: 'Welcome to Nora!',
    body:
      "I'm your personal health AI companion. I'll help you understand your glucose, plan meals, " +
      'and build healthier habits. Let me show you around!',
    icon: 'sparkle',
    iconBackgroundColor: 'theme.primary.base',
    iconColor: 'theme.solid.white',
    showSkip: true,
  },
  {
    id: NoraHomeWalkthroughStepId.AskNora,
    presentation: NoraHomeWalkthroughPresentation.Anchored,
    title: 'Ask Me Anything',
    body:
      'Type a question or tap the mic to speak. I can help with meals, glucose patterns, sleep and more.',
    icon: 'chat-circle',
    iconBackgroundColor: 'theme.success.lightest',
    iconColor: 'theme.primary.base',
    targetId: NoraHomeWalkthroughTargetId.AskNoraInput,
  },
  {
    id: NoraHomeWalkthroughStepId.QuickPrompts,
    presentation: NoraHomeWalkthroughPresentation.Anchored,
    title: 'Quick Prompts',
    body:
      'Shortcuts to common questions. Tap one to start a conversation instantly. You can customize these in Settings.',
    icon: 'lightning',
    iconBackgroundColor: 'theme.info.lightest',
    iconColor: 'theme.primary.base',
    targetId: NoraHomeWalkthroughTargetId.QuickPromptsHeader,
  },
  {
    id: NoraHomeWalkthroughStepId.Settings,
    presentation: NoraHomeWalkthroughPresentation.Anchored,
    title: 'Your Settings',
    body:
      'Customize quick prompts, notifications, chat preferences and more. Make Nora work exactly how you want.',
    icon: 'gear',
    iconBackgroundColor: 'theme.warning.lightest',
    iconColor: 'theme.primary.base',
    targetId: NoraHomeWalkthroughTargetId.Settings,
  },

  {
    id: NoraHomeWalkthroughStepId.Conversations,
    presentation: NoraHomeWalkthroughPresentation.Anchored,
    title: 'Chat History',
    body:
      'All your conversations with me are saved in the Conversations section next to the “See all” button. ' +
      'Pin your favorites so they’re always easy to find.',
    icon: 'chat-circle',
    iconBackgroundColor: 'theme.accent.tea',
    iconColor: 'theme.primary.base',
    targetId: NoraHomeWalkthroughTargetId.ConversationsHeader,
  },
  {
    id: NoraHomeWalkthroughStepId.Capabilities,
    presentation: NoraHomeWalkthroughPresentation.Standalone,
    title: 'What Nora Can Do for You',
    body:
      'Explore the ways Nora can support your health with personalized guidance, insights, and everyday actions.',
    icon: 'sparkle',
    iconBackgroundColor: 'theme.info.lightest',
    iconColor: 'theme.primary.base',
    actions: [
      'Make Sense of Your Glucose Data',
      'Schedule a Call with Your Dietitian',
      'Upload Your Lab Panels',
      'Build a Weekly Strategy',
    ],
  },
]

export const getStepById = (id: NoraHomeWalkthroughStepId): NoraHomeWalkthroughStep => {
  const step = steps.find((configuredStep) => configuredStep.id === id)

  if (!step) {
    throw new Error(`Missing Nora home walkthrough step: ${id}`)
  }

  return step
}

export const getStepIndexById = (id: NoraHomeWalkthroughStepId) => {
  const stepIndex = steps.findIndex((step) => step.id === id)

  if (stepIndex === -1) {
    throw new Error(`Missing Nora home walkthrough step index: ${id}`)
  }

  return stepIndex
}

export const anchoredSteps = steps.filter(
  (step): step is AnchoredStep => step.presentation === NoraHomeWalkthroughPresentation.Anchored,
)

export const getAnchoredStepByTargetId = (targetId: string) =>
  anchoredSteps.find((step) => step.targetId === targetId)

export const getAnchoredStepOrder = (targetId: NoraHomeWalkthroughTargetId) =>
  anchoredSteps.findIndex((step) => step.targetId === targetId) + 1
